// Copyright (c) StrangeBee 2024

import { useLocalMessage } from '@Helpers/locale';
import { columnsRenderer } from '@Hooks/useConfigList/helper/computeHeader';
import { useDrawer } from '@Hooks/useDrawer';
import { FormatPermissions } from '@Molecules/Format';
import { THList } from '@Molecules/THList';
import { UserEventList } from '@Organisms/User/eventList';
import { GlobalUserDetails } from '@Organisms/User/GlobalUserDetails/GlobalUserDetails';
import { GlobalUserDrawer } from '@Organisms/User/GlobalUserDrawer/GlobalUserDrawer';
import React, { ReactElement } from 'react';
import { OrganisationOutput, UserOutput } from 'thehive-sdk';

interface OrganisationUserListProps {
    userEventList: UserEventList;
    userList: UserOutput[];
    organisation: OrganisationOutput;
}

export const OrganisationUserList = ({
    userEventList,
    organisation,
    userList,
}: OrganisationUserListProps): ReactElement => {
    const i = useLocalMessage();

    const { visible, setVisible, data, setData } = useDrawer<UserOutput>({});

    const columns = columnsRenderer<UserOutput>([
        {
            className: 'column_preview',
            render: (data) => (
                <GlobalUserDetails
                    data={data}
                    onPreviewClick={() => {
                        setVisible(true);
                        setData(data);
                    }}
                />
            ),
        },
        {
            title: [{ label: i('user.list.table.columns.profile') }],
            className: 'user-list__column-profile',
            render: ({ profile = '', permissions = [] }) => (
                <FormatPermissions profile={profile} permissions={permissions} />
            ),
        },
    ]);

    useDrawer.watch(userList, () => {
        if (data) {
            setData(userList.find(({ _id }) => _id === data._id));
        }
    });

    return (
        <>
            <THList>
                <THList.Content dataSource={userList} columns={columns} rowKey="_id" size="small" />
            </THList>

            {data && visible && (
                <GlobalUserDrawer
                    visible={visible}
                    onVisibleChange={setVisible}
                    data={data}
                    eventList={userEventList}
                    orgId={organisation.name}
                />
            )}
        </>
    );
};
